// Copyright 2025 Sorama B.V.
import { autoinject, bindable, customElement, TaskQueue } from "aurelia-framework";
import Inputmask from "inputmask";

@customElement("number-input")
@autoinject
export class NumberInput {
    protected inputElement: HTMLInputElement;

    @bindable
    public value: number;

    // A CSS class can be passed to set on the input element
    @bindable
    public inputClass: string;

    // Number of allowed decimal places
    @bindable
    public decimals = 4;

    // Minimum numerical value
    @bindable
    public min?: number;

    // Maximum numerical value
    @bindable
    public max?: number;

    // Placeholder text to display when input field is empty
    @bindable
    public placeholder?: string;

    // Prefix, such as a currency symbol
    @bindable
    public prefix?: string;

    private currentMask;

    constructor(
        private element: Element,
        private taskQueue: TaskQueue
    ) {}

    attached() {
        this.reapplyMask();
    }

    public prefixChanged(): void {
        if (this.currentMask) {
            this.reapplyMask();
        }
    }

    public valueChanged(newValue: number, oldValue: number): void {
        // Use TaskQueue to ensure the event is dispatched after all synchronous operations
        // (including mask updates and value converter processing) have completed
        this.taskQueue.queueMicroTask(() => {
            // Dispatch custom event when value changes
            const event = new CustomEvent("change", {
                detail: {
                    newValue: newValue,
                    oldValue: oldValue,
                },
                bubbles: true,
            });
            this.element.dispatchEvent(event);
        });
    }

    reapplyMask() {
        if (this.currentMask) {
            this.currentMask.remove();
        }

        this.currentMask = new Inputmask({
            alias: "numeric",
            greedy: false,
            radixPoint: ".",
            autoGroup: true,
            rightAlign: false,
            shortcuts: null,
            digits: this.decimals,
            min: this.min,
            max: this.max,
            placeholder: "",
            prefix: this.prefix,
        });

        this.currentMask.mask(this.inputElement);
    }
}
